import React, { useState, useEffect, useRef } from 'react';
import API from '../api/config';
import '../styles/Upload.css';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2004 + 1 }, (_, i) => CURRENT_YEAR - i);

export default function Upload() {
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState('');
  const [subjects,  setSubjects]  = useState([]);
  const [papers,    setPapers]    = useState([]);
  const [form,      setForm]      = useState({ subject_id: '', year: '', exam_type: 'KCET' });
  const fileRef = useRef();

  useEffect(() => { fetchSubjects(); fetchPapers(); }, []);

  const fetchSubjects = async () => {
    try { const res = await API.get('/api/subjects'); setSubjects(res.data); }
    catch (err) { console.error(err); }
  };

  const fetchPapers = async () => {
    try { const res = await API.get('/api/upload/papers'); setPapers(res.data); }
    catch (err) { console.error(err); }
  };

  const handleFileChange = e => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setResult(null); setError('');
    const lower     = f.name.toLowerCase();
    const yearMatch = f.name.match(/(20\d{2})/);
    let detectedExam = 'KCET';
    if (lower.includes('jee') && lower.includes('main')) detectedExam = 'JEE Mains';
    else if (lower.includes('jee'))  detectedExam = 'JEE';
    else if (lower.includes('neet')) detectedExam = 'NEET';
    else if (lower.includes('jsat')) detectedExam = 'JSAT';
    setForm(prev => ({
      ...prev,
      year     : yearMatch ? yearMatch[1] : prev.year,
      exam_type: detectedExam,
    }));
  };

  const handleDrop = e => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (lower.endsWith('.docx') || lower.endsWith('.tex')) {
      handleFileChange({ target: { files: [f] } });
    } else {
      setError('Only .docx and .tex files are supported.');
    }
  };

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleUpload = async () => {
    if (!file)            { setError('Please select a DOCX or TEX file'); return; }
    if (!form.subject_id) { setError('Please select a subject'); return; }
    setUploading(true); setError(''); setResult(null);
    const fd = new FormData();
    fd.append('file',          file);
    fd.append('original_name', file.name);
    fd.append('subject_id',    form.subject_id);
    fd.append('year',          form.year);
    fd.append('exam_type',     form.exam_type);
    try {
      const res = await API.post('/api/upload/file', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      fetchPapers();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDelete = async (paper) => {
    if (!window.confirm(`Delete "${paper.original_filename}" and all its questions?`)) return;
    try {
      await API.delete('/api/upload/papers', {
        data: {
          subject_id: paper.subject_id,
          year      : paper.year,
          exam_type : paper.exam_type,
        },
      });
      fetchPapers();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  // Derive file type label for display
  const fileTypeLabel = file
    ? file.name.toLowerCase().endsWith('.tex') ? 'TEX' : 'DOCX'
    : null;

  return (
    <div className="up-container">
      <div className="up-page-header">
        <h1 className="up-page-title">Upload Question Paper</h1>
      </div>

      <div className="up-card">

        {/* Drop zone */}
        <div
          className={`up-zone ${file ? 'has-file' : ''}`}
          onClick={() => fileRef.current.click()}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
        >
          <div className="up-icon">{file ? '✅' : '📄'}</div>
          {file ? (
            <>
              <h3>{file.name}</h3>
              <p>
                <span className="up-file-type-badge">{fileTypeLabel}</span>
                &nbsp;{(file.size / 1024).toFixed(1)} KB — Click to change
              </p>
            </>
          ) : (
            <>
              <h3>Click to select or drag &amp; drop</h3>
              <p>Supports <strong>.docx</strong> and <strong>.tex</strong> files</p>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".docx,.tex"
            className="up-hidden-input"
            onChange={handleFileChange}
          />
        </div>

        {/* Subject */}
        <div className="up-field">
          <label className="up-label">Subject</label>
          <select className="up-select" name="subject_id" value={form.subject_id} onChange={handleChange}>
            <option value="">— Select Subject —</option>
            {subjects.map(s => (
              <option key={s.subject_id} value={s.subject_id}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Year + Exam Type */}
        <div className="up-row">
          <div className="up-field">
            <label className="up-label">Year</label>
            <select className="up-select" name="year" value={form.year} onChange={handleChange}>
              <option value="">— Select Year —</option>
              {YEAR_OPTIONS.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="up-field">
            <label className="up-label">Exam Type</label>
            <select className="up-select" name="exam_type" value={form.exam_type} onChange={handleChange}>
              <option value="KCET">KCET</option>
              <option value="JSAT">JSAT</option>
              <option value="JEE">JEE</option>
              <option value="JEE Mains">JEE Mains</option>
              <option value="NEET">NEET</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        {error && <div className="up-error">{error}</div>}

        <button className="up-btn" onClick={handleUpload} disabled={uploading || !file}>
          {uploading ? 'Uploading & Parsing...' : 'Upload & Import Questions'}
        </button>

        {uploading && <div className="up-progress"><div className="up-progress-fill" /></div>}

        {result && (
          <div className="up-result">
            <h3>✅ Upload Successful!</h3>
            <p className="up-result-meta">
              {result.exam_type} · {result.year}
              {result.file_type && (
                <span className="up-file-type-badge" style={{ marginLeft: 8 }}>
                  {result.file_type.toUpperCase()}
                </span>
              )}
            </p>
            <div className="up-stats">
              <div className="up-stat">
                <div className="up-stat-num">{result.total}</div>
                <div className="up-stat-label">Found</div>
              </div>
              <div className="up-stat">
                <div className="up-stat-num" style={{ color: '#15803d' }}>{result.inserted}</div>
                <div className="up-stat-label">Inserted</div>
              </div>
              <div className="up-stat">
                <div className="up-stat-num" style={{ color: '#dc2626' }}>{result.skipped}</div>
                <div className="up-stat-label">Skipped</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Uploaded Papers List */}
      {papers.length > 0 && (
        <div className="up-papers-section">
          <h2 className="up-papers-title">Uploaded Papers</h2>
          <div className="up-table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="up-th-num"><strong>Sl. No.</strong></th>
                  <th><strong>File</strong></th>
                  <th><strong>Subject</strong></th>
                  <th><strong>Exam</strong></th>
                  <th><strong>Year</strong></th>
                  <th><strong>Questions</strong></th>
                  <th><strong>Action</strong></th>
                </tr>
              </thead>
              <tbody>
                {papers.map((p, i) => (
                  <tr key={i}>
                    <td className="up-td-num">{i + 1}</td>
                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.original_filename?.toLowerCase().endsWith('.tex') && (
                        <span className="up-file-type-badge" style={{ marginRight: 6 }}>TEX</span>
                      )}
                      {p.original_filename}
                    </td>
                    <td><span className="up-badge">{p.subject_name}</span></td>
                    <td>{p.exam_type}</td>
                    <td>{p.year || '—'}</td>
                    <td>{p.question_count}</td>
                    <td>
                      <button className="up-del-btn" onClick={() => handleDelete(p)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}